// DOM builders for the <sanctum-form> Web Component.
//
// All construction uses the DOM API (createElement / textContent /
// setAttribute) — never innerHTML with untrusted values — per the
// ticket's security non-negotiables.
//
// Every field row is `<div part="field">` → `<label for>` → input control.
// Required fields get `aria-required="true"`. Error slots are
// `<div part="field-error" role="alert">` and are announced when
// populated by render-time errors.

import type { FieldDefinition, FormSchemaResponse } from "./schema";

// Namespaced ID so multiple <sanctum-form> instances on one page don't
// collide on label[for].
let instanceCounter = 0;
export function nextInstanceId(): string {
  instanceCounter += 1;
  return `sf-${instanceCounter}`;
}

export interface RenderContext {
  instanceId: string;
  noAttribution: boolean;
}

// Build a single field row (label + control + error slot). Returns the
// wrapper <div>. For type="hidden" the label + error slot are omitted.
export function buildField(
  field: FieldDefinition,
  ctx: RenderContext,
): HTMLElement {
  if (field.type === "hidden") {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = field.name;
    return input;
  }

  const wrapper = document.createElement("div");
  wrapper.setAttribute("part", "field");
  wrapper.dataset.field = field.name;
  if (field.visible_if?.field) {
    wrapper.dataset.visibleIfField = field.visible_if.field;
    wrapper.dataset.visibleIfEquals = JSON.stringify(field.visible_if.equals);
  }
  // Span the grid based on width hint (#3091). Missing/unknown → full.
  wrapper.style.gridColumn = field.width === "half" ? "span 1" : "span 2";

  const inputId = `${ctx.instanceId}-${field.name}`;

  const control = buildControl(field, inputId);

  // Checkbox labels read more naturally with the label AFTER the box:
  //   [x] I agree to the privacy policy
  if (field.type === "checkbox" || field.type === "toggle") {
    const row = document.createElement("div");
    row.setAttribute("part", "checkbox-row");
    const label = document.createElement("label");
    label.setAttribute("for", inputId);
    label.setAttribute("part", "label");
    label.textContent = field.label;
    row.appendChild(control);
    row.appendChild(label);
    wrapper.appendChild(row);
  } else {
    const label = document.createElement("label");
    label.setAttribute("for", inputId);
    label.setAttribute("part", "label");
    label.textContent = field.label;
    if (field.required) {
      const marker = document.createElement("span");
      marker.setAttribute("part", "required-marker");
      marker.setAttribute("aria-hidden", "true");
      marker.textContent = " *";
      label.appendChild(marker);
    }
    wrapper.appendChild(label);
    wrapper.appendChild(control);
  }

  const errorSlot = document.createElement("div");
  errorSlot.setAttribute("part", "field-error");
  errorSlot.setAttribute("role", "alert");
  errorSlot.dataset.errorFor = field.name;
  // Start empty; submit.ts populates textContent on 422.
  wrapper.appendChild(errorSlot);

  const footer = document.createElement("div");
  footer.setAttribute("part", "field-footer");
  footer.dataset.fieldId = field.name;
  const status = document.createElement("span");
  status.setAttribute("part", "save-status");
  status.className = "state-saved";
  const dot = document.createElement("span");
  dot.setAttribute("part", "save-dot");
  const label = document.createElement("span");
  label.setAttribute("part", "save-label");
  label.textContent = "Saved";
  status.appendChild(dot);
  status.appendChild(label);
  footer.appendChild(status);
  wrapper.appendChild(footer);

  return wrapper;
}

function buildControl(field: FieldDefinition, inputId: string): HTMLElement {
  const baseAttrs = (el: HTMLElement) => {
    el.id = inputId;
    el.setAttribute("name", field.name);
    el.setAttribute("part", "input");
    if (field.required) {
      el.setAttribute("aria-required", "true");
    }
  };

  switch (field.type) {
    case "textarea": {
      const ta = document.createElement("textarea");
      baseAttrs(ta);
      ta.rows = 5;
      if (field.placeholder) ta.placeholder = field.placeholder;
      return ta;
    }
    case "select": {
      const select = document.createElement("select");
      baseAttrs(select);
      if (field.multiple) {
        select.multiple = true;
        select.size = Math.min(6, (field.options || []).length || 4);
        select.setAttribute("part", "input multi");
      }
      // Leading blank option when not required so "no selection" is
      // representable. Required selects use the first real option as
      // the default instead. Multi-select never gets a blank opt.
      if (!field.multiple) {
        if (!field.required && field.placeholder) {
          const placeholderOpt = document.createElement("option");
          placeholderOpt.value = "";
          placeholderOpt.textContent = field.placeholder;
          select.appendChild(placeholderOpt);
        } else if (field.placeholder) {
          const placeholderOpt = document.createElement("option");
          placeholderOpt.value = "";
          placeholderOpt.textContent = field.placeholder;
          placeholderOpt.disabled = true;
          placeholderOpt.selected = true;
          select.appendChild(placeholderOpt);
        }
      }
      for (const opt of field.options ?? []) {
        const o = document.createElement("option");
        o.value = opt.value;
        o.textContent = opt.label;
        select.appendChild(o);
      }
      return select;
    }
    case "checkbox": {
      const input = document.createElement("input");
      baseAttrs(input);
      input.type = "checkbox";
      input.value = "true";
      return input;
    }
    case "checkbox_group": {
      const group = document.createElement("div");
      group.setAttribute("part", "checkbox-group");
      group.setAttribute("role", "group");
      group.setAttribute("aria-label", field.label);
      for (const opt of field.options ?? []) {
        const label = document.createElement("label");
        label.setAttribute("part", "checkbox-option");
        const input = document.createElement("input");
        input.type = "checkbox";
        input.name = field.name;
        input.value = opt.value;
        input.setAttribute("part", "input");
        label.appendChild(input);
        label.appendChild(document.createTextNode(opt.label));
        group.appendChild(label);
      }
      return group;
    }
    case "ordered_list": {
      const container = document.createElement("div");
      container.setAttribute("part", "ordered-list");
      container.setAttribute("role", "group");
      container.setAttribute("aria-label", field.label);

      // Hidden input for form submission + draft persistence
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = field.name;
      hidden.setAttribute("part", "input");
      hidden.value = "[]";
      container.appendChild(hidden);

      // Ranked section
      const rankedSection = document.createElement("div");
      rankedSection.setAttribute("part", "ordered-list-section");
      const rankedLabel = document.createElement("span");
      rankedLabel.setAttribute("part", "ordered-list-section-label");
      rankedLabel.textContent = "Your ranking";
      rankedSection.appendChild(rankedLabel);
      const rankedList = document.createElement("div");
      rankedList.setAttribute("part", "ranked-list");
      rankedSection.appendChild(rankedList);
      container.appendChild(rankedSection);

      // Pool section
      const poolSection = document.createElement("div");
      poolSection.setAttribute("part", "ordered-list-section");
      const poolLabel = document.createElement("span");
      poolLabel.setAttribute("part", "ordered-list-section-label");
      poolLabel.textContent = "Available segments";
      poolSection.appendChild(poolLabel);
      const poolList = document.createElement("div");
      poolList.setAttribute("part", "pool-list");
      poolSection.appendChild(poolList);
      container.appendChild(poolSection);

      // Ghost element (drag preview)
      const ghost = document.createElement("div");
      ghost.setAttribute("part", "drag-ghost");
      ghost.style.display = "none";
      const ghostBadge = document.createElement("span");
      ghostBadge.setAttribute("part", "rank-badge");
      ghost.appendChild(ghostBadge);
      const ghostText = document.createElement("span");
      ghostText.setAttribute("part", "card-text");
      ghost.appendChild(ghostText);
      container.appendChild(ghost);

      // Drop indicator line
      const indicator = document.createElement("div");
      indicator.setAttribute("part", "drop-indicator");
      container.appendChild(indicator);

      // ─── State ───
      const allItems = field.options ?? [];
      const rankedValues: string[] = [];
      let dragSource: HTMLElement | null = null;
      let dragStartIndex = -1;
      let dropIndex = -1;
      let isDragging = false;
      let hasMoved = false;
      let longPressTimer: ReturnType<typeof setTimeout> | null = null;
      let pointerStartX = 0;
      let pointerStartY = 0;

      // ─── Render ───
      function render() {
        const rankedSet = new Set(rankedValues);
        rankedList.textContent = "";
        poolList.textContent = "";

        if (rankedValues.length === 0) {
          const hint = document.createElement("div");
          hint.setAttribute("part", "pool-hint");
          hint.textContent = "Click segments below to build your ranking.";
          rankedList.appendChild(hint);
        } else {
          for (let i = 0; i < rankedValues.length; i++) {
            const item = allItems.find((it) => it.value === rankedValues[i]);
            if (!item) continue;
            const card = document.createElement("div");
            card.setAttribute("part", "ranked-card");
            card.dataset.value = item.value;

            const badge = document.createElement("span");
            badge.setAttribute("part", "rank-badge");
            badge.textContent = String(i + 1);
            card.appendChild(badge);

            const grip = document.createElement("span");
            grip.setAttribute("part", "grip-icon");
            grip.textContent = "\u283f\uFE0E";
            card.appendChild(grip);

            const text = document.createElement("span");
            text.setAttribute("part", "card-text");
            text.textContent = item.label;
            card.appendChild(text);

            const removeBtn = document.createElement("button");
            removeBtn.setAttribute("part", "remove-btn");
            removeBtn.setAttribute("aria-label", "Remove from ranking");
            removeBtn.textContent = "\u2715";
            card.appendChild(removeBtn);

            rankedList.appendChild(card);
          }
        }

        const unranked = allItems.filter((it) => !rankedSet.has(it.value));
        if (unranked.length === 0) {
          const hint = document.createElement("div");
          hint.setAttribute("part", "pool-hint");
          hint.textContent = "All segments ranked. Click \u2715 to remove.";
          poolList.appendChild(hint);
        } else {
          for (const item of unranked) {
            const card = document.createElement("div");
            card.setAttribute("part", "pool-card");
            card.dataset.value = item.value;
            card.setAttribute("role", "button");
            card.setAttribute("tabindex", "0");

            const text = document.createElement("span");
            text.setAttribute("part", "card-text");
            text.textContent = item.label;
            card.appendChild(text);

            poolList.appendChild(card);
          }
        }

        hidden.value = JSON.stringify(rankedValues);
      }

      // ─── Drop indicator helpers ───
      function getInsertionIndex(clientY: number): number {
        const cards = rankedList.querySelectorAll<HTMLElement>('[part="ranked-card"]');
        for (let i = 0; i < cards.length; i++) {
          const r = cards[i].getBoundingClientRect();
          if (clientY < r.top + r.height / 2) return i;
        }
        return cards.length;
      }

      function updateDropIndicator(clientY: number) {
        const cards = rankedList.querySelectorAll<HTMLElement>('[part="ranked-card"]');
        dropIndex = getInsertionIndex(clientY);
        for (const c of cards) c.classList.remove("gap-above", "gap-below");

        if (cards.length === 0) {
          indicator.classList.remove("visible");
          return;
        }

        const listRect = rankedList.getBoundingClientRect();
        if (dropIndex === 0) {
          const firstRect = cards[0].getBoundingClientRect();
          indicator.style.top = `${firstRect.top - listRect.top + rankedList.scrollTop - 2}px`;
          cards[0].classList.add("gap-above");
        } else if (dropIndex >= cards.length) {
          const lastRect = cards[cards.length - 1].getBoundingClientRect();
          indicator.style.top = `${lastRect.bottom - listRect.top + rankedList.scrollTop + 2}px`;
          cards[cards.length - 1].classList.add("gap-below");
        } else {
          cards[dropIndex - 1].classList.add("gap-below");
          cards[dropIndex].classList.add("gap-above");
          const belowRect = cards[dropIndex].getBoundingClientRect();
          indicator.style.top = `${belowRect.top - listRect.top + rankedList.scrollTop - 2}px`;
        }
        indicator.classList.add("visible");
      }

      function clearDropIndicator() {
        indicator.classList.remove("visible");
        rankedList.querySelectorAll<HTMLElement>('[part="ranked-card"]').forEach((c) => {
          c.classList.remove("gap-above", "gap-below");
        });
      }

      // ─── Drag start ───
      function startDrag(card: HTMLElement, clientX: number, clientY: number) {
        const idx = Array.from(rankedList.querySelectorAll('[part="ranked-card"]')).indexOf(card);
        if (idx < 0) return;
        dragSource = card;
        dragStartIndex = idx;
        dropIndex = idx;
        isDragging = true;
        hasMoved = false;
        card.classList.add("dragging");

        const cardRect = card.getBoundingClientRect();
        const badge = card.querySelector('[part="rank-badge"]');
        const text = card.querySelector('[part="card-text"]');
        ghostBadge.textContent = badge?.textContent ?? "";
        ghostText.textContent = text?.textContent ?? "";
        ghost.style.width = `${cardRect.width}px`;
        ghost.style.display = "flex";
        positionGhost(clientX, clientY);
      }

      function positionGhost(clientX: number, clientY: number) {
        ghost.style.left = `${clientX}px`;
        ghost.style.top = `${clientY}px`;
      }

      // ─── Pointer events ───
      rankedList.addEventListener("pointerdown", (e) => {
        const card = (e.target as HTMLElement).closest<HTMLElement>('[part="ranked-card"]');
        if (!card) return;
        if ((e.target as HTMLElement).closest('[part="remove-btn"]')) return;

        pointerStartX = e.clientX;
        pointerStartY = e.clientY;

        if (e.pointerType === "mouse") {
          startDrag(card, e.clientX, e.clientY);
        } else {
          longPressTimer = setTimeout(() => {
            longPressTimer = null;
            startDrag(card, e.clientX, e.clientY);
          }, 500);
        }
      });

      document.addEventListener("pointermove", (e) => {
        if (longPressTimer) {
          const dx = e.clientX - pointerStartX;
          const dy = e.clientY - pointerStartY;
          if (dx * dx + dy * dy > 25) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
          }
          return;
        }
        if (!isDragging || !dragSource) return;
        hasMoved = true;
        e.preventDefault();
        positionGhost(e.clientX, e.clientY);
        updateDropIndicator(e.clientY);
      });

      document.addEventListener("pointerup", () => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
          return;
        }
        if (!isDragging) return;

        ghost.style.display = "none";
        clearDropIndicator();
        if (dragSource) dragSource.classList.remove("dragging");

        if (hasMoved && dropIndex >= 0 && dropIndex !== dragStartIndex) {
          const item = rankedValues.splice(dragStartIndex, 1)[0];
          const target = dropIndex > dragStartIndex ? dropIndex - 1 : dropIndex;
          rankedValues.splice(target, 0, item);
          render();
          hidden.dispatchEvent(new Event("input", { bubbles: true }));
          hidden.dispatchEvent(new Event("change", { bubbles: true }));
        }

        dragSource = null;
        dragStartIndex = -1;
        dropIndex = -1;
        isDragging = false;
        hasMoved = false;
      });

      document.addEventListener("pointercancel", () => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
        if (isDragging) {
          ghost.style.display = "none";
          clearDropIndicator();
          if (dragSource) dragSource.classList.remove("dragging");
          dragSource = null;
          dragStartIndex = -1;
          dropIndex = -1;
          isDragging = false;
          hasMoved = false;
        }
      });

      // ─── Pool click: promote to ranked ───
      poolList.addEventListener("click", (e) => {
        const card = (e.target as HTMLElement).closest<HTMLElement>('[part="pool-card"]');
        if (!card || !card.dataset.value) return;
        rankedValues.push(card.dataset.value);
        render();
        hidden.dispatchEvent(new Event("input", { bubbles: true }));
        hidden.dispatchEvent(new Event("change", { bubbles: true }));
      });

      // ─── Remove from ranked ───
      rankedList.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLElement>('[part="remove-btn"]');
        if (!btn) return;
        const card = btn.closest<HTMLElement>('[part="ranked-card"]');
        if (!card || !card.dataset.value) return;
        const idx = rankedValues.indexOf(card.dataset.value);
        if (idx >= 0) rankedValues.splice(idx, 1);
        render();
        hidden.dispatchEvent(new Event("input", { bubbles: true }));
        hidden.dispatchEvent(new Event("change", { bubbles: true }));
      });

      // ─── Draft restore event ───
      container.addEventListener("sanctum:ordered-list-restore", () => {
        try {
          const parsed = JSON.parse(hidden.value);
          if (Array.isArray(parsed)) {
            rankedValues.length = 0;
            rankedValues.push(...parsed);
          }
        } catch {
          rankedValues.length = 0;
        }
        render();
      });

      // Initial render
      render();

      return container;
    }
    case "rating": {
      const max = (field.validation as Record<string, unknown> | null)?.max
        ? Number((field.validation as Record<string, unknown>).max)
        : 5;
      const wrapper = document.createElement("div");
      wrapper.setAttribute("part", "rating-group");
      wrapper.setAttribute("role", "radiogroup");
      wrapper.setAttribute("aria-label", field.label);
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = field.name;
      hidden.setAttribute("part", "input");
      wrapper.appendChild(hidden);
      const stars: HTMLButtonElement[] = [];
      for (let i = 1; i <= max; i++) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.setAttribute("role", "radio");
        btn.setAttribute("aria-checked", "false");
        btn.dataset.value = String(i);
        btn.setAttribute("aria-label", `${i} star${i > 1 ? "s" : ""}`);
        btn.textContent = "\u2606";
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const val = Number(btn.dataset.value);
          hidden.value = String(val);
          for (let j = 0; j < stars.length; j++) {
            const checked = j < val;
            stars[j].setAttribute("aria-checked", String(checked));
            stars[j].textContent = checked ? "\u2605" : "\u2606";
            stars[j].setAttribute("part", checked ? "rating-star active" : "rating-star");
          }
        });
        stars.push(btn);
        wrapper.appendChild(btn);
      }
      return wrapper;
    }
    case "signature": {
      const wrapper = document.createElement("div");
      wrapper.setAttribute("part", "signature-wrapper");
      const canvas = document.createElement("canvas");
      canvas.setAttribute("part", "signature-canvas");
      canvas.width = 400;
      canvas.height = 150;
      canvas.setAttribute("aria-label", field.label);
      canvas.setAttribute("tabindex", "0");
      const ctx = canvas.getContext("2d");
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = field.name;
      hidden.setAttribute("part", "input");
      let drawing = false;
      let hasContent = false;
      const penColor = ((field.validation as Record<string, unknown> | null)?.pen_color as string) || "#1c1917";
      if (ctx) {
        ctx.strokeStyle = penColor;
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
      const startDraw = (e: MouseEvent | Touch) => {
        drawing = true;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);
        ctx?.beginPath();
        ctx?.moveTo(x, y);
      };
      const doDraw = (e: MouseEvent | Touch) => {
        if (!drawing) return;
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);
        ctx?.lineTo(x, y);
        ctx?.stroke();
        hasContent = true;
      };
      const stopDraw = () => {
        if (!drawing) return;
        drawing = false;
        ctx?.closePath();
        hidden.value = hasContent ? canvas.toDataURL("image/png") : "";
        canvas.dispatchEvent(new Event("input", { bubbles: true }));
      };
      canvas.addEventListener("mousedown", (e) => startDraw(e));
      canvas.addEventListener("mousemove", (e) => doDraw(e));
      canvas.addEventListener("mouseup", stopDraw);
      canvas.addEventListener("mouseleave", stopDraw);
      canvas.addEventListener("touchstart", (e) => { e.preventDefault(); startDraw(e.touches[0]); });
      canvas.addEventListener("touchmove", (e) => { doDraw(e.touches[0]); });
      canvas.addEventListener("touchend", stopDraw);
      const toolbar = document.createElement("div");
      toolbar.setAttribute("part", "signature-toolbar");
      const clearBtn = document.createElement("button");
      clearBtn.type = "button";
      clearBtn.textContent = "Clear";
      clearBtn.setAttribute("part", "signature-clear");
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        hasContent = false;
        hidden.value = "";
        canvas.dispatchEvent(new Event("input", { bubbles: true }));
      });
      toolbar.appendChild(clearBtn);
      wrapper.appendChild(canvas);
      wrapper.appendChild(toolbar);
      wrapper.appendChild(hidden);
      return wrapper;
    }
    case "toggle": {
      const input = document.createElement("input");
      baseAttrs(input);
      input.type = "checkbox";
      input.setAttribute("role", "switch");
      input.value = "true";
      return input;
    }
    case "radio": {
      const group = document.createElement("div");
      group.setAttribute("part", "radio-group");
      for (const opt of field.options ?? []) {
        const label = document.createElement("label");
        label.setAttribute("part", "radio-option");
        const input = document.createElement("input");
        input.type = "radio";
        input.name = field.name;
        input.value = opt.value;
        input.setAttribute("part", "input");
        label.appendChild(input);
        label.appendChild(document.createTextNode(opt.label));
        group.appendChild(label);
      }
      return group;
    }
    case "combobox": {
      const wrapper = document.createElement("div");
      wrapper.setAttribute("part", "combobox-wrapper");
      const input = document.createElement("input");
      baseAttrs(input);
      input.type = "text";
      input.setAttribute("autocomplete", "off");
      input.setAttribute("list", inputId + "-list");
      if (field.placeholder) input.placeholder = field.placeholder;
      const datalist = document.createElement("datalist");
      datalist.id = inputId + "-list";
      for (const opt of field.options ?? []) {
        const o = document.createElement("option");
        o.value = opt.label;
        datalist.appendChild(o);
      }
      wrapper.appendChild(input);
      wrapper.appendChild(datalist);
      return wrapper;
    }
    case "range": {
      const wrapper = document.createElement("div");
      wrapper.setAttribute("part", "range-wrapper");
      const input = document.createElement("input");
      baseAttrs(input);
      input.type = "range";
      if (field.validation) {
        const v = field.validation as Record<string, unknown>;
        if (v.min !== undefined) input.min = String(v.min);
        if (v.max !== undefined) input.max = String(v.max);
        if (v.step !== undefined) input.step = String(v.step);
      }
      const display = document.createElement("span");
      display.setAttribute("part", "range-value");
      display.textContent = input.value || input.min || "0";
      input.addEventListener("input", () => {
        display.textContent = input.value;
      });
      wrapper.appendChild(input);
      wrapper.appendChild(display);
      return wrapper;
    }
    case "file": {
      const wrapper = document.createElement("div");
      wrapper.setAttribute("part", "file-wrapper");
      const input = document.createElement("input");
      baseAttrs(input);
      input.type = "file";
      if (field.validation) {
        const v = field.validation as Record<string, unknown>;
        if (typeof v.accept === "string") input.accept = v.accept;
      }
      const info = document.createElement("span");
      info.setAttribute("part", "file-info");
      info.setAttribute("aria-live", "polite");
      input.addEventListener("change", () => {
        const file = input.files?.[0];
        info.textContent = file ? `${file.name} (${formatFileSize(file.size)})` : "";
      });
      wrapper.appendChild(input);
      wrapper.appendChild(info);
      return wrapper;
    }
    case "password":
    case "number":
    case "date":
    case "time":
    case "datetime-local":
    case "url":
    case "color":
    case "email":
    case "tel":
    case "text":
    default: {
      const input = document.createElement("input");
      baseAttrs(input);
      // Unknown → text (element.ts logs a console warning).
      const nativeInputTypes = new Set([
        "number", "date", "time", "datetime-local", "url", "color",
        "email", "tel", "password",
      ]);
      input.type = nativeInputTypes.has(field.type) ? field.type : "text";
      // Skipped on types that ignore placeholder (browser silently
      // disregards it, but suppressing the attribute avoids test noise
      // and keeps the DOM clean).
      const noPlaceholder = new Set(["date", "time", "datetime-local", "color", "range", "password", "file"]);
      if (field.placeholder && !noPlaceholder.has(field.type)) {
        input.placeholder = field.placeholder;
      }
      return input;
    }
  }
}

function buildMoveButton(
  direction: "up" | "down",
  label: string,
  row: HTMLElement,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("part", `ordered-list-${direction}`);
  button.setAttribute("aria-label", label);
  button.textContent = direction === "up" ? "↑" : "↓";
  button.addEventListener("click", () => {
    const parent = row.parentElement;
    if (!parent) return;
    if (direction === "up" && row.previousElementSibling) {
      parent.insertBefore(row, row.previousElementSibling);
    } else if (direction === "down" && row.nextElementSibling) {
      parent.insertBefore(row.nextElementSibling, row);
    }
    row.dispatchEvent(new Event("input", { bubbles: true }));
    row.dispatchEvent(new Event("change", { bubbles: true }));
  });
  return button;
}

// Build the honeypot input. Off-screen positioning + aria-hidden +
// tabindex="-1" per the security non-negotiables (NOT display:none).
export function buildHoneypot(fieldName: string): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "text";
  input.name = fieldName;
  input.setAttribute("tabindex", "-1");
  input.setAttribute("aria-hidden", "true");
  input.setAttribute("autocomplete", "off");
  input.setAttribute("part", "honeypot");
  input.dataset.honeypot = "true";
  return input;
}

// Hidden until the server requests adaptive verification. Keeping a stable
// live region in the form lets screen readers receive the prompt before the
// managed widget takes focus.
export function buildChallengeRegion(): HTMLElement {
  const region = document.createElement("section");
  region.setAttribute("part", "challenge");
  region.dataset.role = "challenge";
  region.hidden = true;

  const message = document.createElement("p");
  message.setAttribute("part", "challenge-message");
  message.setAttribute("role", "status");
  message.setAttribute("aria-live", "polite");
  message.dataset.role = "challenge-message";
  region.appendChild(message);

  const widget = document.createElement("div");
  widget.setAttribute("part", "challenge-widget");
  widget.dataset.role = "challenge-widget";
  region.appendChild(widget);
  return region;
}

// Build the "Powered by Sanctum Forms" attribution footer, unless the
// embedder opted out via the no-attribution attribute.
export function buildAttribution(noAttribution: boolean): HTMLElement | null {
  if (noAttribution) return null;
  const footer = document.createElement("div");
  footer.setAttribute("part", "attribution");
  const link = document.createElement("a");
  link.href = "https://digitalsanctum.com.au";
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.textContent = "Secure submission · Powered by Sanctum Forms";
  footer.appendChild(link);
  return footer;
}

// Build the paused-state notice (when schema.status === "paused").
export function buildPausedNotice(): HTMLElement {
  const p = document.createElement("p");
  p.setAttribute("role", "status");
  p.setAttribute("part", "paused");
  p.textContent = "This form is not currently accepting submissions.";
  return p;
}

// Build the success notice (shown after a 2xx submit).
export function buildSuccessNotice(message: string): HTMLElement {
  const p = document.createElement("p");
  p.setAttribute("role", "status");
  p.setAttribute("aria-live", "polite");
  p.setAttribute("part", "success");
  p.textContent = message;
  return p;
}

// Build the form-level error banner (rendered at the top of the form).
export function buildFormError(message: string): HTMLElement {
  const div = document.createElement("div");
  div.setAttribute("role", "alert");
  div.setAttribute("part", "form-error");
  div.textContent = message;
  return div;
}

// Assemble the full form DOM from a schema response. Returns the root
// <form> element with all rows, honeypot, submit button, and optional
// attribution footer attached.
export function buildForm(
  schema: FormSchemaResponse,
  ctx: RenderContext,
): HTMLFormElement {
  const form = document.createElement("form");
  form.setAttribute("part", "form");
  form.setAttribute("novalidate", "");

  // Form-level error banner slot (empty until submit populates it).
  // role="alert" matches the field-scoped error pattern (buildField above)
  // so screen readers announce 429 / 403 / network / timeout / unknown
  // errors when submit.ts populates textContent. AC-6.
  const errorBanner = document.createElement("div");
  errorBanner.setAttribute("part", "form-error-banner");
  errorBanner.setAttribute("role", "alert");
  errorBanner.dataset.role = "form-error-banner";
  form.appendChild(errorBanner);

  let lastSection: string | null | undefined = undefined;
  for (const field of schema.field_schema) {
    if (!isKnownType(field.type)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[sanctum-form] unknown field type "${field.type}" for "${field.name}"; rendering as text`,
      );
    }
    if (field.section_title && field.section_title !== lastSection) {
      lastSection = field.section_title;
      const header = document.createElement("div");
      header.setAttribute("part", "section-header");
      const h2 = document.createElement("h2");
      h2.setAttribute("part", "section-header-title");
      h2.textContent = field.section_title;
      header.appendChild(h2);
      form.appendChild(header);
    } else if (!field.section_title) {
      lastSection = null;
    }
    form.appendChild(buildField(field, ctx));
  }

  // Honeypot: always present, always off-screen.
  form.appendChild(buildHoneypot(schema.settings.honeypot_field_name));
  form.appendChild(buildChallengeRegion());

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.setAttribute("part", "submit");
  // #3105 — submit-button customisation. Per-surface defaults
  // ("Send Message" here) preserve byte-identical legacy markup when
  // the form was saved before #3105 (the keys are absent from the
  // public schema response). Width/alignment classes are stamped only
  // when non-default so legacy buttons carry no submit-w-*/submit-a-*
  // classes.
  const submitLabel = schema.settings.submit_label || "Send Message";
  submit.textContent = submitLabel;
  const submitClasses: string[] = [];
  if (schema.settings.submit_width === "half") {
    submitClasses.push("submit-w-half");
  }
  const alignment = schema.settings.submit_alignment;
  if (alignment === "center" || alignment === "right") {
    submitClasses.push(`submit-a-${alignment}`);
  }
  if (submitClasses.length > 0) {
    submit.className = submitClasses.join(" ");
  }
  // Cache the configured label so element.ts's post-error reset path
  // doesn't clobber a custom label with the per-surface default.
  submit.dataset.defaultLabel = submitLabel;
  form.appendChild(submit);

  const attribution = buildAttribution(ctx.noAttribution);
  if (attribution) form.appendChild(attribution);

  wireConditionalFields(form);
  return form;
}

const KNOWN_TYPES = new Set<string>([
  "text",
  "email",
  "tel",
  "select",
  "textarea",
  "checkbox",
  "hidden",
  "number",
  "date",
  "time",
  "datetime-local",
  "url",
  "color",
  "range",
  "password",
  "toggle",
  "radio",
  "combobox",
  "rating",
  "signature",
  "file",
  "checkbox_group",
  "ordered_list",
]);

function isKnownType(t: string): boolean {
  return KNOWN_TYPES.has(t);
}

function currentFieldValue(
  form: HTMLFormElement,
  name: string,
): string | boolean | string[] | null {
  const controls = Array.from(form.elements).filter(
    (el): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
      (el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement) &&
      el.name === name,
  );
  if (controls.length === 0) return null;
  const first = controls[0];
  if (first instanceof HTMLInputElement && first.type === "checkbox") {
    const checked = controls.filter(
      (el): el is HTMLInputElement => el instanceof HTMLInputElement && el.checked,
    );
    if (controls.length === 1) return checked.length === 1;
    return checked.map((el) => el.value);
  }
  if (first instanceof HTMLSelectElement && first.multiple) {
    return Array.from(first.selectedOptions).map((o) => o.value);
  }
  return first.value;
}

function matchesVisibleIf(
  actual: string | boolean | string[] | null,
  expected: string | boolean | string[],
): boolean {
  if (Array.isArray(actual)) {
    if (Array.isArray(expected)) return expected.some((v) => actual.includes(v));
    return actual.includes(String(expected));
  }
  if (Array.isArray(expected)) return expected.includes(String(actual));
  return actual === expected || String(actual) === String(expected);
}

function updateConditionalFields(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLElement>("[data-visible-if-field]").forEach(
    (wrapper) => {
      const field = wrapper.dataset.visibleIfField;
      if (!field) return;
      let expected: string | boolean | string[] = "";
      try {
        expected = JSON.parse(wrapper.dataset.visibleIfEquals ?? '""') as
          | string
          | boolean
          | string[];
      } catch {
        expected = wrapper.dataset.visibleIfEquals ?? "";
      }
      const visible = matchesVisibleIf(currentFieldValue(form, field), expected);
      wrapper.hidden = !visible;
      wrapper
        .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
          "input, textarea, select",
        )
        .forEach((control) => {
          control.disabled = !visible;
        });
    });
}

function wireConditionalFields(form: HTMLFormElement): void {
  if (!form.querySelector("[data-visible-if-field]")) return;
  const refresh = () => updateConditionalFields(form);
  form.addEventListener("input", refresh);
  form.addEventListener("change", refresh);
  form.addEventListener("sanctum:conditional-refresh", refresh);
  refresh();
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
